import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
  QueryState,
  StatusBadge,
} from "@nymbl/nos2-rules";
import type { UseQueryResult } from "@tanstack/react-query";
import { type CapacityBar, capacityBar, capacityChip, monthRow } from "@/lib/month/metrics";
import { type MonthKey, monthName } from "@/lib/month/month";
import { formatHours } from "@/lib/time/rules";
import type { MonthSummaryView } from "@/schemas/month";

function Bullet({ bar }: { bar: CapacityBar }) {
  const aria = `Guidance ${formatHours(bar.planned)} hours plus ${formatHours(bar.pto)} PTO hours against ${formatHours(bar.capacity)} hours capacity`;
  return (
    <div className="flex flex-col gap-3">
      <div role="img" aria-label={aria} className="relative mx-6 h-16">
        <div className="absolute inset-x-0 top-6 h-5 rounded-md bg-muted" />
        <div
          className="absolute top-6 left-0 h-5 rounded-l-md bg-chart-1"
          style={{ width: `${bar.plannedPct}%` }}
        />
        <div
          className="absolute top-6 h-5 rounded-r-md bg-chart-4"
          style={{ left: `${bar.plannedPct}%`, width: `${bar.ptoPct}%` }}
        />
        <div
          className="absolute top-4 h-9 w-0.5 -translate-x-1/2 rounded bg-foreground"
          style={{ left: `${bar.markerPct}%` }}
        />
        <div
          className="absolute top-12 -translate-x-1/2 whitespace-nowrap text-xs font-bold"
          style={{ left: `${bar.markerPct}%` }}
        >
          {formatHours(bar.capacity)} capacity
        </div>
      </div>
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-chart-1" aria-hidden="true" />
          <strong className="text-foreground">{formatHours(bar.planned)}</strong> guidance
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-chart-4" aria-hidden="true" />
          <strong className="text-foreground">{formatHours(bar.pto)}</strong> PTO
        </span>
      </div>
    </div>
  );
}

/** Guidance + PTO against capacity; "N hrs over" equals guidance - (capacity - PTO). */
export function CapacityPanel({
  month,
  query,
}: {
  month: MonthKey;
  query: UseQueryResult<MonthSummaryView>;
}) {
  const row = monthRow(query.data, month, query.isPlaceholderData);
  const bar = row ? capacityBar(row) : null;
  const chip = bar ? capacityChip(bar) : null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2 className="font-display text-lg font-bold">
            {monthName(row?.month ?? month)} capacity
          </h2>
        </CardTitle>
        {chip && (
          <CardAction>
            <StatusBadge
              status={chip.tone}
              tones={{ over: "danger" }}
              labels={{ [chip.tone]: chip.text }}
            />
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        <QueryState query={query} skeletonClassName="h-24">
          {() =>
            bar ? (
              <Bullet bar={bar} />
            ) : (
              <p className="text-sm text-muted-foreground">No capacity set</p>
            )
          }
        </QueryState>
      </CardContent>
    </Card>
  );
}
